import { Box, Dialog, DialogContent, DialogTitle, Link, Typography } from "@mui/material";
import type { Client } from "@/types";
import { InlineClientSelector } from "./ClientGate";

interface ClientPickerDialogProps {
  open: boolean;
  selectedClient: Client | null;
  onClose: () => void;
  onClientChange: (client: Client | null) => void;
  /** "Wrong file? Reject it…" — opens the reject dialog from here (#7441). */
  onRejectRequest?: () => void;
}

/**
 * "Change client" from the review header (#7431 AC1). Wraps the same
 * server-searched picker the ClientGate banner uses, so the two entry points
 * behave identically. Closes on a successful pick. With a client already set
 * the picker is replace-only (no clear): the tab re-seeds the client from the
 * document record, so a clear would only flash the gate and snap back. The richer client dialog with
 * suggestions is #7434 and will replace this body, not this entry point.
 */
export const ClientPickerDialog = ({
  open,
  selectedClient,
  onClose,
  onClientChange,
  onRejectRequest,
}: ClientPickerDialogProps) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="client-picker-title">
    <DialogTitle id="client-picker-title" variant="h3" sx={{ pb: 0.5 }}>
      Choose a client
      <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
        Claimants and claim lines load against this client&apos;s active policy.
      </Typography>
    </DialogTitle>
    <DialogContent sx={{ pt: 2, pb: 3 }}>
      {/* Replace-only: the header picker opens for a document that already
          has a client, and "no client" is not a holdable state (see
          InlineClientSelector.disableClearable). */}
      <InlineClientSelector
        selectedClient={selectedClient}
        disableClearable={!!selectedClient}
        onClientChange={(client) => {
          if (!client) return;
          onClientChange(client);
          onClose();
        }}
      />
      {onRejectRequest && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Link
            component="button"
            type="button"
            variant="body2"
            color="text.secondary"
            onClick={() => {
              onClose();
              onRejectRequest();
            }}
          >
            Wrong file? Reject it…
          </Link>
        </Box>
      )}
    </DialogContent>
  </Dialog>
);
